import {
  type TabAccessibilityHandle,
  type TabOrderEntry,
  initTabAccessibility,
} from '@suntex.core/a11y';

import type { Hub } from './display/Hub';
import {
  RADIAL_KEYBOARD_BLUR_EVENT,
  RADIAL_KEYBOARD_FOCUS_EVENT,
  type Radial,
} from './display/Radial';
import { BUTTON_RADIUS } from './display/const';

const BASE_CENTRALIZE_ANIMATION_MS = 250;
const TAB_ORDER_REBUILD_DELAY_MS = 50;

export function getAccessibleName(radial: Pick<Radial, 'ariaLabel'>): string {
  return radial.ariaLabel;
}

export function getAccessibleTitle(
  radial: Pick<
    Radial,
    'ariaLabel' | 'enabled' | 'inFocus' | 'underConstruction'
  >,
  position: 'center' | 'child' | 'breadcrumb',
): string {
  const name = getAccessibleName(radial);

  if (radial.underConstruction) {
    return `${name}, under construction`;
  }

  if (!radial.enabled || !radial.inFocus) {
    return `${name}, disabled`;
  }

  if (position === 'center') {
    return `${name}, go back`;
  }

  if (position === 'breadcrumb') {
    return `${name}, breadcrumb`;
  }

  return name;
}

export function isAccessibilityActionAvailable(
  radial: Pick<Radial, 'display' | 'enabled' | 'inFocus' | 'underConstruction'>,
): boolean {
  return (
    radial.display &&
    radial.enabled &&
    radial.inFocus &&
    !radial.underConstruction
  );
}

function createTabOrderEntry(
  radial: Radial,
  position: 'center' | 'child' | 'breadcrumb',
): TabOrderEntry {
  return {
    obj: radial.button,
    title: getAccessibleTitle(radial, position),
    disabled: !isAccessibilityActionAvailable(radial),
  };
}

export function buildTabOrder(hub: Hub): TabOrderEntry[] {
  const central = hub.centralRadial as Radial | null;
  if (!central) return [];

  const tabOrder: TabOrderEntry[] = [];

  if (central.radialParent && central.display) {
    tabOrder.push(createTabOrderEntry(central, 'center'));
  }

  const children = [...central.radialChildren]
    .filter((radial) => radial.display && !radial.hidden)
    .sort((a, b) => a.angle - b.angle);

  for (const child of children) {
    tabOrder.push(createTabOrderEntry(child, 'child'));
  }

  for (const ancestor of central.radialAncestors) {
    if (ancestor.display && !ancestor.hidden) {
      tabOrder.push(createTabOrderEntry(ancestor, 'breadcrumb'));
    }
  }

  return tabOrder;
}

export function initAccessibility(
  hub: Hub,
  stage: createjs.Stage,
  onError?: (error: unknown) => void,
): TabAccessibilityHandle {
  return initTabAccessibility({
    appName: 'FIM Hub',
    stage,
    getTabOrder: () => buildTabOrder(hub),
    subscribe: (rebuild) => hub.subscribeCentralize(rebuild),
    rebuildDelayMs: () =>
      hub.animationDurationFactor * BASE_CENTRALIZE_ANIMATION_MS +
      TAB_ORDER_REBUILD_DELAY_MS,
    getFallbackBounds: () =>
      new createjs.Rectangle(
        -BUTTON_RADIUS,
        -BUTTON_RADIUS,
        BUTTON_RADIUS * 2,
        BUTTON_RADIUS * 2,
      ),
    onEntryFocus: (obj) => obj.dispatchEvent(RADIAL_KEYBOARD_FOCUS_EVENT),
    onEntryBlur: (obj) => obj.dispatchEvent(RADIAL_KEYBOARD_BLUR_EVENT),
    // Radials already animate their own keyboard-focus feedback (reusing the
    // hover/scale tween); the generic overlay would draw a second, redundant
    // indicator on top of it.
    focusHighlight: false,
    onError,
  });
}
